import React from 'react';

/** Shared pill/chip styling for multi-select fields (imputations, admin user form). */
export const SELECTION_CHIP_COLORS = {
    border: 'hsl(38, 30%, 90%)',
    background: 'hsl(38, 100%, 95%)',
    ring: 'hsl(38, 100%, 75%)',
    avatarBackground: 'hsl(38, 100%, 48%)',
    label: 'hsl(38, 60%, 25%)',
    remove: 'hsl(38, 50%, 45%)',
};

export const SELECTION_CHIP_LIST_SPACING = '12px';

export function selectionChipListStyle() {
    return {
        display: 'flex',
        flexDirection: 'column',
        gap: '8px',
        marginTop: SELECTION_CHIP_LIST_SPACING,
        paddingTop: SELECTION_CHIP_LIST_SPACING,
        borderTop: `1px solid ${SELECTION_CHIP_COLORS.border}`,
    };
}

export function selectionChipRowStyle() {
    return {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        background: SELECTION_CHIP_COLORS.background,
        border: `1px solid ${SELECTION_CHIP_COLORS.ring}`,
        borderRadius: '12px',
        padding: '8px 12px',
        fontSize: '0.85rem',
        fontWeight: 500,
    };
}

export function selectionChipAvatarProps(label) {
    const displayLabel = label == null || label === '' ? '—' : String(label);
    return {
        initial: displayLabel.charAt(0).toUpperCase(),
        style: {
            width: 28,
            height: 28,
            borderRadius: '50%',
            background: SELECTION_CHIP_COLORS.avatarBackground,
            color: 'white',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '0.75rem',
            fontWeight: 700,
            marginRight: 10,
            flexShrink: 0,
        },
    };
}

export function selectionChipDefaultBadgeStyle() {
    return {
        marginLeft: 8,
        padding: '2px 8px',
        borderRadius: '999px',
        fontSize: '0.72rem',
        fontWeight: 600,
        color: SELECTION_CHIP_COLORS.label,
        background: 'white',
        border: `1px solid ${SELECTION_CHIP_COLORS.ring}`,
    };
}

/** Compact horizontal pill chips (imputation modal approvers / departments). */
export function selectionChipPillListStyle() {
    return {
        display: 'flex',
        flexWrap: 'wrap',
        gap: '8px',
        marginTop: '12px',
        paddingTop: '12px',
        borderTop: `1px solid ${SELECTION_CHIP_COLORS.border}`,
    };
}

export function selectionChipPillRowStyle() {
    return {
        display: 'flex',
        alignItems: 'center',
        background: SELECTION_CHIP_COLORS.background,
        border: `1px solid ${SELECTION_CHIP_COLORS.ring}`,
        borderRadius: '20px',
        padding: '5px 12px 5px 5px',
        fontSize: '0.78rem',
        fontWeight: 500,
    };
}

export function selectionChipPillAvatarProps(label) {
    const displayLabel = label == null || label === '' ? '—' : String(label);
    return {
        initial: displayLabel.charAt(0).toUpperCase(),
        style: {
            width: 24,
            height: 24,
            borderRadius: '50%',
            background: SELECTION_CHIP_COLORS.avatarBackground,
            color: 'white',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '0.7rem',
            fontWeight: 700,
            marginRight: 8,
            flexShrink: 0,
        },
    };
}

export function selectionChipPillLabelStyle() {
    return {
        color: SELECTION_CHIP_COLORS.label,
    };
}

export function selectionChipRemoveButtonStyle() {
    return {
        marginLeft: 8,
        lineHeight: 1,
        color: SELECTION_CHIP_COLORS.remove,
        fontSize: '1rem',
        fontWeight: 400,
    };
}

export function renderSelectionChipPill({ key, label, canEdit, onRemove }) {
    const avatar = selectionChipPillAvatarProps(label);
    return (
        <div key={key} style={selectionChipPillRowStyle()}>
            <div style={avatar.style}>{avatar.initial}</div>
            <span style={selectionChipPillLabelStyle()}>{label}</span>
            {canEdit ? (
                <button
                    type="button"
                    className="btn p-0"
                    style={selectionChipRemoveButtonStyle()}
                    onClick={onRemove}
                >×</button>
            ) : null}
        </div>
    );
}

/** Full-width stacked row (admin user form adjoints / timekeepers pattern). */
export function renderSelectionChipRow({ key, label, canEdit, onRemove, removeLabel = '×' }) {
    const avatar = selectionChipAvatarProps(label);
    return (
        <div key={key} style={selectionChipRowStyle()}>
            <div className="d-flex align-items-center flex-wrap">
                <div style={avatar.style}>{avatar.initial}</div>
                <span style={{ color: SELECTION_CHIP_COLORS.label }}>{label}</span>
            </div>
            {canEdit ? (
                <button
                    type="button"
                    className="btn btn-outline-secondary btn-sm ml-2 flex-shrink-0"
                    onClick={onRemove}
                >
                    {removeLabel}
                </button>
            ) : null}
        </div>
    );
}
